import { ISmartrrShopTheme } from "@smartrr/shared/shopifyRest/theme";
import {
  fetchShopifyThemes,
  ThemeValidationAssetFiles,
  validateShopifyTheme,
} from "@vendor-app/app/AdminRoute/AdminSetupRoute/libs/utils/themes";
import { typedFrontendVendorApi } from "@vendor-app/utils/typedFrontendVendorApi";
import { navigateWithShopInQuery } from "@vendor-app/utils/navigateWithShopInQuery";
import { adminConfigRoutePrefix } from "@smartrr/shared/constants";
import { loadSellingPlanGroups } from "@vendor-app/app/AdminRoute/AdminSetupRoute/libs/hooks/useAdminSetupRouteRemoteData";
import { ensureShopifyGid } from "@smartrr/shared/utils/ensureShopifyGid";

import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { ISetup } from "@smartrr/shared/entities/Organization";

import { NestedPath, PathValue, setNestedProperty } from "@smartrr/shared/utils/setNestedProperty";
import { cloneDeep } from "lodash";
import { GettingStartedChecklist } from "../GettingStartedChecklist";
import { GettingStartedStepFactory } from "../GettingStartedChecklist/GettingStartedStepFactory";

export type SetupStep =
  | "initialize"
  | "customerPortal"
  | "subscriptionBlock"
  | "subscriptionProgram"
  | "migrationService";
interface GettingStartedStore {
  loading: Record<SetupStep, boolean>;
  themes: ISmartrrShopTheme[];
  themeValidation: {
    isThemeValid: boolean;
    isProductFileValid: boolean;
    isAccountFileValid: boolean;
  };
  chosenThemeId: string;
  setup: ISetup | null;
  sellingPlanGroup: {
    productIds: string[];
    variantIds: string[];
  };
  displayVerifyImplementationText: boolean;
  error: {
    showError: boolean;
    message: string;
  };
  actions: {
    initialize(initializeAll?: boolean): Promise<void>;
    update(
      updates: { path: NestedPath<ISetup>; val: any }[],
      step: SetupStep,
      options?: {
        installingSnippets?: boolean;
        installingWidgets?: boolean;
      }
    ): void;
    setChosenThemeId(id: string): void;
    setIsThemeValid(isValid: boolean): void;
    setIsProductFileValid(isValid: boolean): void;
    setIsAccountFileValid(isValid: boolean): void;
    setDisplayVerifyImplementationText(shouldDisplay: boolean): void;
    createNewSellingPlanGroup(): void;
    validateTheme(themeId: string | number, file: ThemeValidationAssetFiles): Promise<boolean>;
    setSubscriptionSetup(subscriptionSetup: ISetup["subscriptionSetup"]): void;
    fetchSellingPlanProductsAndVariants(sellingPlanId: string | undefined): void;
    setIsLoading(isLoading: boolean, step: SetupStep): void;
  };
  internal: {
    whileStepIsLoading(step: SetupStep, loadFn: () => Promise<void>): Promise<void>;
    fetchThemes(): Promise<void>;
    fetchSetup(): Promise<ISetup | null>;
    clearError(): void;
    logError(message: string): void;
  };
}

const useGettingStartedStore = create<GettingStartedStore>()(
  immer((set, get) => ({
    loading: {
      initialize: false,
      customerPortal: false,
      subscriptionProgram: false,
      subscriptionBlock: false,
      migrationService: false,
    },
    themes: [],
    themeValidation: {
      isThemeValid: false,
      isProductFileValid: false,
      isAccountFileValid: false,
    },
    isThemeValid: true,
    isThemeFileValid: false,
    chosenThemeId: "",
    setup: null,
    sellingPlanGroup: {
      productIds: [],
      variantIds: [],
    },
    displayVerifyImplementationText: false,
    error: {
      showError: false,
      message: "",
    },
    selectedStep: null,
    actions: {
      async initialize() {
        const { whileStepIsLoading, fetchSetup, fetchThemes } = get().internal;
        const { fetchSellingPlanProductsAndVariants, setChosenThemeId } = get().actions;

        await whileStepIsLoading("initialize", async () => {
          const setup = await fetchSetup();
          fetchSellingPlanProductsAndVariants(setup?.selectedSellingPlanId);
          await fetchThemes();

          // The saved theme may have been deleted from Shopify since it was chosen
          const savedThemeId = Number(setup?.snippetsThemeId);
          const savedTheme = get().themes.find(theme => theme.id === savedThemeId);

          if (savedTheme !== undefined) {
            setChosenThemeId(String(savedTheme.id));
          }
        });
      },

      setChosenThemeId(id) {
        set(draft => {
          draft.chosenThemeId = id;
        });
      },

      setIsThemeValid(isValid) {
        set(draft => {
          draft.themeValidation.isThemeValid = isValid;
        });
      },

      setIsProductFileValid(isValid) {
        set(draft => {
          draft.themeValidation.isProductFileValid = isValid;
        });
      },

      setIsAccountFileValid(isValid) {
        set(draft => {
          draft.themeValidation.isAccountFileValid = isValid;
        });
      },

      setDisplayVerifyImplementationText(shouldDisplay) {
        set(draft => {
          draft.displayVerifyImplementationText = shouldDisplay;
        });
      },

      async update(
        updates: { path: NestedPath<ISetup>; val: PathValue<ISetup, NestedPath<ISetup>> }[],
        step,
        options?: {
          installingSnippets?: boolean;
          installingWidgets?: boolean;
        }
      ) {
        const { setup, internal } = get();
        if (setup) {
          await internal.whileStepIsLoading(step, async () => {
            const clonedSetup = cloneDeep(setup); // setNestedProperty mutates the provided obj
            for (const { path, val } of updates) {
              setNestedProperty(clonedSetup, path, val);
            }
            const res = await typedFrontendVendorApi.putReq("/setup", {
              reqBody: {
                setup: clonedSetup,
                ...options,
              },
            });

            if (res.type === "success") {
              set({
                setup: res.body,
              });
              return;
            }
            internal.logError("Error updating shop's setup");
          });
        }
      },

      async validateTheme(id, file) {
        if (!id) {
          return true;
        }
        const { internal } = get();
        const themeId = +id;
        let isValid = true;
        try {
          await internal.whileStepIsLoading("subscriptionBlock", async () => {
            const res = await validateShopifyTheme(themeId, file);
            isValid = res;
          });
        } catch {
          internal.logError("Error validating theme, please try again.");
        }
        return isValid;
      },
      createNewSellingPlanGroup() {
        navigateWithShopInQuery(
          `${adminConfigRoutePrefix}/plans/create`,
          undefined,
          {
            replace: true,
            hash: "fromGettingStarted",
          },
          false
        );
      },
      setSubscriptionSetup(subscriptionSetup) {
        const { setup } = get();

        set({
          setup: {
            ...setup,
            subscriptionSetup,
          } as ISetup,
        });
      },

      async fetchSellingPlanProductsAndVariants(sellingPlanId) {
        const { actions, internal, setup } = get();
        const { setSubscriptionSetup } = actions;

        const sellingPlanGroups = await loadSellingPlanGroups();

        const usedSellingPlanGroup =
          sellingPlanGroups.find(group => group.shopifyId === sellingPlanId) ?? sellingPlanGroups[0];
        const targetSellingPlanId = usedSellingPlanGroup.shopifyId!;

        if (!targetSellingPlanId) {
          internal.logError("No Selling plan groups found");
          return;
        }

        const { productIds, productVariantIds: variantIds } = usedSellingPlanGroup;
        const productGIDs = productIds.map(p => ensureShopifyGid("Product", p));
        const variantGIDs = variantIds.map(v => ensureShopifyGid("ProductVariant", v));

        if (setup) {
          setSubscriptionSetup({
            sellingPlan: {
              completed: !!usedSellingPlanGroup,
              date: setup.subscriptionSetup.sellingPlan?.date,
              demo: setup.subscriptionSetup.sellingPlan?.demo,
            },
            product: {
              completed: !!usedSellingPlanGroup.productIds.length,
              date: setup.subscriptionSetup.product?.date,
              demo: setup.subscriptionSetup.product?.demo,
            },
          });
        }

        set({
          sellingPlanGroup: {
            productIds: productGIDs,
            variantIds: variantGIDs,
          },
        });
      },

      setIsLoading(isLoading, step) {
        set(draft => {
          draft.loading[step] = isLoading;
        });
      },
    },
    internal: {
      async whileStepIsLoading(step: SetupStep, loadFn: () => Promise<void>) {
        set(draft => {
          draft.loading[step] = true;
        });
        await loadFn();
        set(draft => {
          draft.loading[step] = false;
        });
      },

      async fetchThemes() {
        const { themes: storedThemes, internal } = get();
        try {
          if (!storedThemes.length) {
            const themes = await fetchShopifyThemes();
            set({
              themes,
            });
          }
        } catch (error) {
          internal.logError(error.message);
        }
      },

      async fetchSetup() {
        const { setup: storedSetup, internal } = get();
        if (!storedSetup) {
          const res = await typedFrontendVendorApi.getReq("/setup");

          if (res.type === "success") {
            internal.clearError();
            set({
              setup: res.body,
            });
            return res.body;
          }

          internal.logError("Error fetching shop's setup");
        }
        return storedSetup;
      },

      clearError() {
        set({
          error: {
            message: "",
            showError: false,
          },
        });
      },

      logError(message) {
        set(draft => ({
          ...draft,
          error: {
            showError: true,
            message: draft.error.message ? `${draft.error.message}, ${message}` : message,
          },
        }));
      },
    },
  }))
);

const initialState = useGettingStartedStore.getState();

export const GettingStartedStoreAccess = {
  useLoading: (step: SetupStep) => useGettingStartedStore(state => state.loading[step]),
  useThemes: () => useGettingStartedStore(state => state.themes),
  useChosenThemeId: () => useGettingStartedStore(state => state.chosenThemeId),
  useThemeValidation: () => useGettingStartedStore(state => state.themeValidation),
  useSetup: () => useGettingStartedStore(state => state.setup),
  useChecklist: () =>
    useGettingStartedStore(state =>
      state.setup ? new GettingStartedChecklist(new GettingStartedStepFactory(state.setup).generateSteps()) : null
    ),
  useSellingPlanGroup: () => useGettingStartedStore(state => state.sellingPlanGroup),
  useDisplayVerifyImplementationText: () =>
    useGettingStartedStore(state => state.displayVerifyImplementationText),
  useErrors: () => useGettingStartedStore(state => state.error),
  useActions: () => useGettingStartedStore(state => state.actions),
  testing: {
    state: useGettingStartedStore.getState,
    actions: useGettingStartedStore.getState().actions,
    reset(state?: Partial<GettingStartedStore>) {
      useGettingStartedStore.setState({ ...initialState, ...state });
    },
  },
};
